import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { FiX } from "react-icons/fi";
import Button from "../../../../../components/ui/Button";
import InputField from "../../../../../components/ui/InputField";
import { useTheme } from "../../../../../context/ThemeContext";
import { DietaryPreference } from "../../../../../types/dietary";


interface DietaryPreferenceFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: Pick<DietaryPreference, 'name' | 'description' | 'ar'>) => Promise<void>;
  initialData?: Pick<DietaryPreference, 'name' | 'description' | 'ar'>;
  isLoading?: boolean;
}

const DietaryPreferenceFormModal: React.FC<DietaryPreferenceFormModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  initialData,
  isLoading = false,
}) => {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    ar: {
      name: "",
      description: "",
    },
  });
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  useEffect(() => {
    if (isOpen) {
      if (initialData) {
        setFormData({
          name: initialData.name,
          description: initialData.description || "",
          ar: initialData.ar || { name: "", description: "" },
        });
      } else {
        setFormData({
          name: "",
          description: "",
          ar: { name: "", description: "" },
        });
      }
      setErrors({});
    }
  }, [isOpen, initialData]);

  const validate = () => {
    const newErrors: { [key: string]: string } = {};

    if (!formData.name.trim()) {
      newErrors.name = t("dietaryPreferences.nameRequired");
    }

    if (!formData.description.trim()) {
      newErrors.description = t("dietaryPreferences.descriptionRequired");
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    try {
      await onSubmit(formData);
      onClose();
    } catch (error) {
    }
  };

  const handleChange = (field: string, value: string) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: "",
      }));
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/80  transition-opacity"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div
          className={`relative w-full max-w-lg transform overflow-hidden rounded-lg shadow-xl transition-all ${theme === "dark" ? "bg-gray-800" : "bg-white"
            }`}
        >
          {/* Header */}
          <div
            className={`flex items-center justify-between border-b px-6 py-4 ${theme === "dark" ? "border-gray-700" : "border-gray-200"
              }`}
          >
            <h3
              className={`text-lg font-semibold ${theme === "dark" ? "text-white" : "text-gray-900"
                }`}
            >
              {initialData
                ? t("dietaryPreferences.editPreference")
                : t("dietaryPreferences.addNewPreference")}
            </h3>
            <button
              onClick={onClose}
              className={`rounded-lg p-1 transition-colors ${theme === "dark"
                  ? "hover:bg-gray-700 text-gray-400"
                  : "hover:bg-gray-100 text-gray-500"
                }`}
            >
              <FiX className="h-5 w-5" />
            </button>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="px-6 py-4">
            <div className="space-y-4">
              {/* Name Field */}
              <div>
                <label
                  htmlFor="name"
                  className={`mb-1 block text-sm font-medium ${theme === "dark" ? "text-gray-200" : "text-gray-700"
                    }`}
                >
                  {t("dietaryPreferences.name")}
                </label>
                <InputField
                  id="name"
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleChange("name", e.target.value)}
                  placeholder={t("dietaryPreferences.namePlaceholder")}
                  error={errors.name}
                  className="w-full"
                />
              </div>

              {/* Description Field */}
              <div>
                <label
                  htmlFor="description"
                  className={`mb-1 block text-sm font-medium ${theme === "dark" ? "text-gray-200" : "text-gray-700"
                    }`}
                >
                  {t("dietaryPreferences.description")}
                </label>
                <textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => handleChange("description", e.target.value)}
                  placeholder={t("dietaryPreferences.descriptionPlaceholder")}
                  rows={3}
                  className={`w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary ${theme === "dark"
                      ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                      : "bg-white border-gray-300 text-gray-900 placeholder-gray-400"
                    } ${errors.description ? "border-red-500" : ""}`}
                />
                {errors.description && (
                  <p className="mt-1 text-xs text-red-500">{errors.description}</p>
                )}
              </div>

              {/* Arabic Name Field */}
              <div>
                <label
                  htmlFor="ar-name"
                  className={`mb-1 block text-sm font-medium ${theme === "dark" ? "text-gray-200" : "text-gray-700"
                    }`}
                >
                  {t("dietaryPreferences.arabicName")}
                </label>
                <InputField
                  id="ar-name"
                  type="text"
                  value={formData.ar.name}
                  onChange={(e) => setFormData((prev) => ({
                    ...prev,
                    ar: { ...prev.ar, name: e.target.value }
                  }))}
                  placeholder={t("dietaryPreferences.arabicNamePlaceholder")}
                  className="w-full"
                />
              </div>

              {/* Arabic Description Field */}
              <div>
                <label
                  htmlFor="ar-description"
                  className={`mb-1 block text-sm font-medium ${theme === "dark" ? "text-gray-200" : "text-gray-700"
                    }`}
                >
                  {t("dietaryPreferences.arabicDescription")}
                </label>
                <textarea
                  id="ar-description"
                  value={formData.ar.description}
                  onChange={(e) => setFormData((prev) => ({
                    ...prev,
                    ar: { ...prev.ar, description: e.target.value }
                  }))}
                  placeholder={t("dietaryPreferences.arabicDescriptionPlaceholder")}
                  rows={3}
                  className={`w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary ${theme === "dark"
                      ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                      : "bg-white border-gray-300 text-gray-900 placeholder-gray-400"
                    }`}
                />
              </div>
            </div>

            {/* Footer */}
            <div className="mt-6 flex justify-end gap-3">
              <Button
                type="submit"
                className="bg-primary hover:bg-primary/90"
                disabled={isLoading}
              >
                {isLoading ? (
                  <div className="flex items-center gap-2">
                    <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                    {t("dietaryPreferences.save")}
                  </div>
                ) : (
                  t("dietaryPreferences.save")
                )}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                disabled={isLoading}
              >
                {t("dietaryPreferences.cancel")}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default DietaryPreferenceFormModal;
